import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { UserPlus, Eye, EyeOff } from "lucide-react";

const GAME_TYPES = [
  "Spades", "Whist", "Bridge", "Backgammon", "Chess", "Checkers", "Poker", "Other"
];

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    full_name: "",
    phone: "",
    gaming_preferences: [],
  });

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate("/home");
    }
  }, [isAuthenticated, navigate]);

  const toggleGamePreference = (game) => {
    setFormData(prev => ({
      ...prev,
      gaming_preferences: prev.gaming_preferences.includes(game)
        ? prev.gaming_preferences.filter(g => g !== game)
        : [...prev.gaming_preferences, game]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        phone: formData.phone,
        gaming_preferences: formData.gaming_preferences,
      });
      toast.success("Registration successful! Welcome to Board2Boston™");
      navigate("/home");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen py-8 px-4 flex items-center justify-center"
      style={{
        background: "linear-gradient(135deg, #050505 0%, #0A0A0A 50%, #050505 100%)"
      }}
      data-testid="register-page"
    >
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-[#A1A1AA] font-ubuntu mb-4">
            Already have an account?{" "}
            <Link to="/login" className="text-[#7a6200] hover:text-[#C6A300] transition-colors font-medium">
              Sign In
            </Link>
          </p>
          <img
            src="https://customer-assets.emergentagent.com/job_hoop-invite/artifacts/r8lcaocz_Board2Boston%20favicon.png"
            alt="Board2Boston"
            className="h-18 mx-auto mb-4"
            style={{ height: '72px' }}
          />
          <h1 className="font-bebas text-4xl tracking-wider text-white">
            CREATE YOUR ACCOUNT
          </h1>
          <p className="text-[#A1A1AA] font-ubuntu mt-2">
            Join our premier gaming community
          </p>
          <p className="text-[#A1A1AA] font-ubuntu mt-1">
            Already have an account?{" "}
            <Link to="/login" className="text-[#7a6200] hover:text-[#C6A300] transition-colors font-medium">
              Sign In
            </Link>
          </p>
        </div>

        <Card className="bg-[#0A0A0A] border border-white/10">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  Full Name *
                </label>
                <Input
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="input-dark h-12"
                  required
                  data-testid="register-fullname"
                />
              </div>

              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-dark h-12"
                  required
                  data-testid="register-email"
                />
              </div>

              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  placeholder="(Optional) Enter your phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-dark h-12"
                  data-testid="register-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  Password *
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-dark h-12 pr-12"
                    required
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  Confirm Password *
                </label>
                <Input
                  type="password"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="input-dark h-12"
                  required
                  data-testid="register-confirm-password"
                />
              </div>

              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-3">
                  Gaming Preferences (Select all that apply)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {GAME_TYPES.map((game) => {
                    const isSelected = formData.gaming_preferences.includes(game);
                    return (
                      <button
                        key={game}
                        type="button"
                        className={`flex items-center gap-2 p-3 rounded cursor-pointer transition-all text-left ${
                          isSelected
                            ? "bg-[#7a6200]/20 border border-[#7a6200]"
                            : "bg-black/30 border border-white/5 hover:border-white/20"
                        }`}
                        onClick={() => toggleGamePreference(game)}
                        data-testid={`game-pref-${game.toLowerCase()}`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                          isSelected ? "bg-[#7a6200] border-[#7a6200]" : "border-[#7a6200]"
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          )}
                        </div>
                        <span className="text-sm font-ubuntu text-white">{game}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Button
                type="submit"
                className="btn-gold w-full h-14 text-lg"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    CREATING ACCOUNT...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5" />
                    CREATE ACCOUNT
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-[#A1A1AA] font-ubuntu">
                Already have an account?{" "}
                <Link to="/login" className="text-[#7a6200] hover:text-[#C6A300] transition-colors">
                  Sign In
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-[#52525B] text-xs font-ubuntu mt-6">
          By registering, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
